import React from 'react';
import { addMonths } from 'date-fns';
import Text from '@zg-rentals/multibrand-ui/lib/Text';
import Row from '@zg-rentals/multibrand-ui/lib/Row';
import Col from '@zg-rentals/multibrand-ui/lib/Col';
import SpacerRow from '@zg-rentals/multibrand-ui/lib/SpacerRow';
import Select from '@zg-rentals/multibrand-ui/lib/Select';
import Label from '@zg-rentals/multibrand-ui/lib/Label';
import { coerceToDate, formatDate } from '@common/paymentsUtils/dateUtils';
import DebouncedButton from '@common/components/DebouncedButton';

type Props = {
  tenantFirstName: string;
  firstAvailableEndDate: string;
  onSubmit: (...args: any) => void;
};

type State = {
  selectedNumberOfPayments: number;
  finalPaymentDue: string;
};

class CurrentTenantM2MShortened extends React.Component<Props, State> {
  options: Array<{ finalPaymentDue: string; numberOfPayments: number }>;

  constructor(props) {
    super(props);
    this.options = [
      {
        finalPaymentDue: formatDate(props.firstAvailableEndDate, 'MMMM dd, yyyy'),
        numberOfPayments: 1,
      },
      {
        finalPaymentDue: formatDate(addMonths(coerceToDate(props.firstAvailableEndDate)!, 1), 'MMMM dd, yyyy'),
        numberOfPayments: 2,
      },
    ];
    this.state = {
      selectedNumberOfPayments: this.options[0].numberOfPayments,
      finalPaymentDue: this.options[0].finalPaymentDue,
    };
  }

  onChangeSelectedNumberOfPayments = (e): void => {
    const index = e.currentTarget.selectedIndex;
    this.setState({
      selectedNumberOfPayments: this.options[index].numberOfPayments,
      finalPaymentDue: this.options[index].finalPaymentDue,
    });
  };

  getSelectOptions = (): React.ReactNode => {
    const options = this.options.map((option) => (
      <option value={option.numberOfPayments} key={option.finalPaymentDue}>
        {option.finalPaymentDue}
      </option>
    ));
    return <>{options}</>;
  };

  onSubmit = () => {
    const { onSubmit } = this.props;
    const numberOfPayments = this.state.selectedNumberOfPayments;
    const finalPaymentDue = this.state.finalPaymentDue;

    return Promise.resolve().then(() => onSubmit({ finalPaymentDue, numberOfPayments }));
  };

  render(): React.ReactNode {
    const { tenantFirstName } = this.props;
    const { selectedNumberOfPayments } = this.state;
    let tenantFirstNamePossessive;
    try {
      const tenantFirstNameAsArray = tenantFirstName.split('');
      const tenantFirstNameLastLetter = tenantFirstNameAsArray[tenantFirstNameAsArray.length - 1];
      tenantFirstNamePossessive = `${tenantFirstName}'${tenantFirstNameLastLetter === 's' ? '' : 's'}`;
    } catch {
      tenantFirstNamePossessive = "Your current tenant's";
    }

    return (
      <>
        <Row>
          <Col>
            <Text type="h3">{tenantFirstName} has a month-to-month term. When would you like it to end?</Text>
          </Col>
        </Row>
        <Row>
          <Col>
            <Label data-auto-test-id="payment-end-date-label">Choose an end date</Label>
            <Select
              data-id="selectedNumberOfPayments"
              value={selectedNumberOfPayments}
              onChange={this.onChangeSelectedNumberOfPayments}
            >
              {this.getSelectOptions()}
            </Select>
            <SpacerRow>
              <Text type="body-small">
                Your next term cannot start before this date.&nbsp;
                {tenantFirstNamePossessive} final payment will be due&nbsp;
                {formatDate(this.state.finalPaymentDue, 'MMMM dd, yyyy')}.
              </Text>
            </SpacerRow>
          </Col>
        </Row>
        <Row>
          <Col>
            <DebouncedButton type="primary" full onClick={this.onSubmit}>
              Continue
            </DebouncedButton>
          </Col>
        </Row>
      </>
    );
  }
}

export default CurrentTenantM2MShortened;
